// AI Meeting Prep — shown on the Client page only when an upcoming Google
// Calendar meeting is matched to this client. Progressive disclosure:
// collapsed header → "Prepare for meeting" → structured brief.
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { format, formatDistanceToNow, isToday, isTomorrow } from "date-fns";
import {
  Video, Loader2, RefreshCw, ArrowUpRight, ChevronDown, ChevronUp, Sparkles, AlertCircle,
} from "lucide-react";
import { toast } from "sonner";

function whenLabel(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const t = format(d, "h:mm a");
  if (isToday(d)) return `Today · ${t}`;
  if (isTomorrow(d)) return `Tomorrow · ${t}`;
  return `${format(d, "EEE d MMM")} · ${t}`;
}

const REF_TAB = { approval: "approvals", deliverable: "deliverables", comment: "deliverables",
                  activity: "activity", project: "progress", signal: "overview" };
const REF_LABEL = { approval: "View approval", deliverable: "View deliverable", comment: "View thread",
                    activity: "View activity", project: "View progress", signal: "View health" };

export default function MeetingPrepPanel({ clientId, onOpenTab }) {
  const [params] = useSearchParams();
  const wantedMeeting = params.get("meeting_id");
  const [meeting, setMeeting] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(!!wantedMeeting);
  const pollRef = useRef(null);

  const loadStatus = useCallback(async (mid) => {
    const { data } = await api.get(`/meetings/${mid}/prep`);
    setStatus(data);
    return data;
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const q = wantedMeeting ? `?meeting_id=${encodeURIComponent(wantedMeeting)}` : "";
        const { data } = await api.get(`/clients/${clientId}/meetings/next${q}`);
        if (cancelled) return;
        setMeeting(data.meeting);
        if (data.meeting) await loadStatus(data.meeting.meeting_id);
      } catch { if (!cancelled) setMeeting(null); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [clientId, wantedMeeting, loadStatus]);

  // Poll while another request holds the generation lock.
  useEffect(() => {
    const generating = status?.prep?.status === "generating";
    if (generating && meeting && !pollRef.current) {
      pollRef.current = setInterval(() => loadStatus(meeting.meeting_id).catch(() => {}), 3000);
    }
    if (!generating && pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [status?.prep?.status, meeting, loadStatus]);

  const generate = async (force = false) => {
    if (!meeting || busy) return;
    setBusy(true);
    setOpen(true);
    try {
      const { data } = await api.post(`/meetings/${meeting.meeting_id}/prep`, { force });
      setStatus(data);
      if (data.prep?.status === "failed") toast.error(data.prep.error || "Couldn't generate the brief");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.detail || "Couldn't generate the brief");
      if (d?.meeting_state) setStatus(s => ({ ...(s || {}), meeting_state: d.meeting_state }));
    } finally { setBusy(false); }
  };

  if (loading || !meeting) return null;

  const prep = status?.prep;
  const mstate = status?.meeting_state || "upcoming";
  const generating = busy || prep?.status === "generating";
  const ready = prep?.status === "ready" && prep.content;

  return (
    <section data-testid="meeting-prep-panel"
             className="rounded-xl border border-slate-200 bg-white overflow-hidden mb-4">
      <div className="px-5 py-4 flex items-center gap-3 flex-wrap">
        <div className="w-9 h-9 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center shrink-0">
          <Video className="w-4 h-4 text-slate-700" />
        </div>
        <div className="flex-1 min-w-[180px]">
          <p className="overline mb-0.5">Upcoming meeting</p>
          <p data-testid="meeting-prep-title" className="font-medium text-slate-900 leading-tight truncate">
            {meeting.title || "Client meeting"}
          </p>
          <p data-testid="meeting-prep-when" className="text-xs text-slate-500 mt-0.5">
            {whenLabel(meeting.start)}
            {meeting.attendees?.length ? ` · ${meeting.attendees.length} attendee${meeting.attendees.length === 1 ? "" : "s"}` : ""}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {meeting.meet_url && (
            <a href={meeting.meet_url} target="_blank" rel="noreferrer" data-testid="meeting-prep-join"
               className="text-sm font-medium border border-slate-200 hover:border-slate-300 px-3 py-2 rounded-lg inline-flex items-center gap-1.5 text-slate-700 transition-colors">
              Join <ArrowUpRight className="w-3.5 h-3.5" />
            </a>
          )}
          {mstate === "upcoming" && !ready && !generating && (
            <button data-testid="meeting-prep-generate" onClick={() => generate(false)}
                    disabled={status?.sufficient_data === false}
                    className="btn-terra text-sm font-medium px-4 py-2 rounded-lg inline-flex items-center gap-2 disabled:opacity-50">
              <Sparkles className="w-4 h-4" /> Prepare for meeting
            </button>
          )}
          {generating && (
            <span data-testid="meeting-prep-generating"
                  className="text-sm text-slate-600 inline-flex items-center gap-2 px-3 py-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Preparing your brief…
            </span>
          )}
          {ready && !generating && (
            <button data-testid="meeting-prep-toggle" onClick={() => setOpen(v => !v)}
                    className="text-sm font-medium border border-slate-200 hover:border-slate-300 px-3 py-2 rounded-lg inline-flex items-center gap-1.5 text-slate-700 transition-colors">
              {open ? <><ChevronUp className="w-4 h-4" /> Hide brief</> : <><ChevronDown className="w-4 h-4" /> View brief</>}
            </button>
          )}
        </div>
      </div>

      {status?.sufficient_data === false && !ready && (
        <Note testid="meeting-prep-insufficient">
          Not enough ClientSpace activity yet to prepare a brief — add deliverables, approvals or comments for this client first.
        </Note>
      )}
      {mstate === "past" && <Note testid="meeting-prep-past">This meeting has already taken place.</Note>}
      {mstate === "unmatched" && <Note testid="meeting-prep-unmatched">This meeting isn't linked to a client, so no brief can be prepared.</Note>}
      {prep?.status === "failed" && !generating && (
        <div data-testid="meeting-prep-failed"
             className="border-t border-slate-100 px-5 py-3 flex items-center gap-3 text-sm text-rose-800 bg-rose-50/60">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span className="flex-1">{prep.error || "We couldn't generate the brief."}</span>
          <button data-testid="meeting-prep-retry" onClick={() => generate(true)}
                  className="font-medium underline-offset-2 hover:underline">Try again</button>
        </div>
      )}

      {ready && open && (
        <PrepBody prep={prep} stale={status.stale} onRefresh={() => generate(true)}
                  busy={generating} clientId={clientId} onOpenTab={onOpenTab} />
      )}
    </section>
  );
}

function Note({ children, testid }) {
  return <p data-testid={testid} className="border-t border-slate-100 px-5 py-3 text-sm text-slate-500">{children}</p>;
}

function RefLink({ ref: r, onOpenTab }) {
  if (!r) return null;
  const tab = REF_TAB[r.type];
  if (!tab) return null;
  return (
    <button data-testid={`prep-ref-${r.type}`} onClick={() => onOpenTab?.(tab, r)}
            className="text-xs text-slate-500 hover:text-[#C55A46] inline-flex items-center gap-0.5 transition-colors">
      {REF_LABEL[r.type]} <ArrowUpRight className="w-3 h-3" />
    </button>
  );
}

function Block({ label, children, testid }) {
  return (
    <div data-testid={testid}>
      <p className="overline mb-2">{label}</p>
      {children}
    </div>
  );
}

function PrepBody({ prep, stale, onRefresh, busy, onOpenTab }) {
  const c = prep.content;
  const [more, setMore] = useState(false);
  const when = prep.generated_at ? formatDistanceToNow(new Date(prep.generated_at), { addSuffix: true }) : "";
  const hasMore = (c.questions?.length || 0) + (c.recent_developments?.length || 0) + (c.risks_opportunities?.length || 0) > 0;
  return (
    <div data-testid="meeting-prep-body" className="border-t border-slate-100 px-5 py-5 space-y-7">
      <div className="flex items-center justify-between gap-3 flex-wrap -mt-1">
        <p className="text-xs text-slate-400 inline-flex items-center gap-1.5">
          AI brief · generated {when}
          {stale && <span data-testid="meeting-prep-stale" className="ml-1 px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">Client data changed since</span>}
        </p>
        <button data-testid="meeting-prep-refresh" onClick={onRefresh} disabled={busy}
                className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 disabled:opacity-50">
          <RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} /> Refresh brief
        </button>
      </div>

      <Block label="Executive brief" testid="prep-executive-brief">
        <p className="text-[15px] text-slate-800 leading-relaxed max-w-3xl">{c.executive_brief}</p>
      </Block>

      {c.focus?.length > 0 && (
        <Block label="Recommended focus" testid="prep-focus">
          <ol className="space-y-4">
            {c.focus.map((f, i) => (
              <li key={i} className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-[#C55A46] text-white text-xs font-medium flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-slate-900">{f.title}</p>
                  <p className="text-sm text-slate-600 mt-0.5 leading-snug max-w-2xl">{f.why}</p>
                  {f.refs?.[0] && <div className="mt-1"><RefLink ref={f.refs[0]} onOpenTab={onOpenTab} /></div>}
                </div>
              </li>
            ))}
          </ol>
        </Block>
      )}

      {c.open_items?.length > 0 && (
        <Block label="Open items" testid="prep-open-items">
          <ul className="space-y-2.5">
            {c.open_items.map((o, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <span className="mt-2 w-1.5 h-1.5 rounded-full bg-slate-400 shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm text-slate-800">{o.label}</p>
                  {o.detail && <p className="text-xs text-slate-500 mt-0.5">{o.detail}</p>}
                  <RefLink ref={o.ref} onOpenTab={onOpenTab} />
                </div>
              </li>
            ))}
          </ul>
        </Block>
      )}

      {hasMore && (
        <div className="border-t border-slate-100 pt-4">
          <button data-testid="meeting-prep-more" onClick={() => setMore(v => !v)}
                  className="text-sm font-medium text-slate-600 hover:text-slate-900 inline-flex items-center gap-1.5 transition-colors">
            {more ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            {more ? "Less" : "More meeting intelligence"}
            {!more && <span className="text-xs text-slate-400 font-normal ml-1">
              {[c.questions?.length && "questions", c.recent_developments?.length && "recent developments",
                c.risks_opportunities?.length && "risks"].filter(Boolean).join(" · ")}
            </span>}
          </button>
          {more && (
            <div data-testid="meeting-prep-more-body" className="mt-5 space-y-7">
              {c.questions?.length > 0 && (
                <Block label="Suggested questions" testid="prep-questions">
                  <ul className="space-y-2.5">
                    {c.questions.map((q, i) => (
                      <li key={i} className="text-sm text-slate-800 pl-3 border-l-2 border-slate-200 leading-snug max-w-2xl">{q}</li>
                    ))}
                  </ul>
                </Block>
              )}
              {c.recent_developments?.length > 0 && (
                <Block label="Recent developments" testid="prep-recent">
                  <ul className="space-y-1.5">
                    {c.recent_developments.map((r, i) => <li key={i} className="text-sm text-slate-700">{r}</li>)}
                  </ul>
                </Block>
              )}
              {c.risks_opportunities?.length > 0 && (
                <Block label="Risks & opportunities" testid="prep-risks">
                  <ul className="space-y-2">
                    {c.risks_opportunities.map((r, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-sm">
                        <span className={`mt-0.5 text-[10px] px-1.5 py-0.5 rounded-full border shrink-0 ${
                          r.kind === "opportunity" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                                   : "bg-amber-50 text-amber-800 border-amber-200"}`}>
                          {r.kind === "opportunity" ? "Opportunity" : "Risk"}
                        </span>
                        <span className="text-slate-800 leading-snug">{r.text}</span>
                      </li>
                    ))}
                  </ul>
                </Block>
              )}
            </div>
          )}
        </div>
      )}

      <p className="text-[11px] text-slate-400 pt-1 border-t border-slate-100">
        Grounded in this client's ClientSpace data only — approvals, deliverables, comments, activity and health signals. Recommendations are suggestions, not facts.
      </p>
    </div>
  );
}
